import { TextField } from '@mui/material';
import { addMonths, eachDayOfInterval, endOfMonth, format, isSameDay, parse, startOfMonth, subMonths } from 'date-fns';
import { enGB, fr } from 'date-fns/locale';
import React, { useEffect, useRef, useState } from 'react';

interface CustomDatePickerProps {
  label: string;
  value: Date | null;
  onChange: (date: Date | null) => void;
  minDate?: Date | null;
  maxDate?: Date | null;
  width?: string;
}

const CustomDatePicker: React.FC<CustomDatePickerProps> = ({
  label,
  value,
  onChange,
  minDate,
  maxDate,
  width = '100%',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const calendarRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (value) {
      setInputValue(format(value, 'dd/MM/yyyy', { locale: enGB }));
      setCurrentMonth(value);
    } else {
      setInputValue('');
    }
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);

    if (newValue.length === 10) {
      try {
        const parsedDate = parse(newValue, 'dd/MM/yyyy', new Date(), { locale: fr });
        if (!isNaN(parsedDate.getTime())) {
          onChange(parsedDate);
        }
      } catch (error) {
        onChange(null);
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      setIsOpen(false);
    }
  };

  const daysInMonth = eachDayOfInterval({
    start: startOfMonth(currentMonth),
    end: endOfMonth(currentMonth),
  });

  const nextMonth = () => setCurrentMonth(addMonths(currentMonth, 1));
  const prevMonth = () => setCurrentMonth(subMonths(currentMonth, 1));

  const isDateDisabled = (date: Date) => {
    if (minDate && date < minDate) return true;
    if (maxDate && date > maxDate) return true;
    return false;
  };

  return (
    <div style={{ position: 'relative' }} ref={calendarRef}>
      <TextField
        fullWidth
        label={label}
        dir="ltr"
        autoComplete='off'
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => setIsOpen(true)}
        placeholder="DD/MM/YYYY"
        InputProps={{
          sx: {
            height: '51px',
          },
          startAdornment: (
            <button
              onClick={() => setIsOpen(!isOpen)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '5px',
              }}
            >📅
            </button>
          ),
        }}
      />
      {isOpen && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            backgroundColor: 'white',
            border: '1px solid #ddd',
            borderRadius: '4px',
            padding: '15px',
            zIndex: 1000,
            boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
            marginTop: '5px',
          }}
        >
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '15px',
            padding: '0 10px',
            direction: 'ltr'
          }}>
            <button
              onClick={prevMonth}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '16px',
                padding: '5px 10px',
                color: '#1976d2',
              }}
            >
              ◀
            </button>
            <span style={{ 
              fontWeight: 'bold',
              color: '#1976d2'
            }}>
              {format(currentMonth, 'MMMM yyyy', { locale: enGB })}
            </span>
            <button
              onClick={nextMonth}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '16px',
                padding: '5px 10px',
                color: '#1976d2',
              }}
            >
              ▶
            </button>
          </div>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(7, 1fr)',
            gap: '5px',
            textAlign: 'center'
          }}>
            {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map(day => (
              <div key={day} style={{ 
                fontWeight: 'bold', 
                fontSize: '12px',
                color: '#666',
                padding: '8px 0'
              }}>
                {day}
              </div>
            ))}
            {daysInMonth.map((day) => {
              const isSelected = value && isSameDay(day, value);
              const isDisabled = isDateDisabled(day);
              
              return (
                <button
                  key={day.toString()}
                  onClick={() => {
                    if (!isDisabled) {
                      onChange(day);
                      setIsOpen(false);
                    }
                  }}
                  style={{
                    padding: '8px',
                    border: 'none',
                    background: isSelected ? '#1976d2' : 'transparent',
                    color: isSelected ? 'white' : isDisabled ? '#ccc' : '#333',
                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                    borderRadius: '4px',
                    fontSize: '14px',
                    transition: 'background-color 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    if (!isDisabled && !isSelected) {
                      e.currentTarget.style.backgroundColor = 'rgba(25, 118, 210, 0.04)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isDisabled && !isSelected) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                  disabled={isDisabled}
                >
                  {format(day, 'd')}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomDatePicker; 